import React, { useRef, useState, useEffect } from "react";
import { RotateCcw } from "lucide-react";

/**
 * SignaturePad — HTML5 canvas signature capture.
 * Uses Pointer Events (works for mouse + touch + stylus, and inside Radix Dialogs
 * where raw touch events get intercepted by the scroll-lock). Emits a base64 PNG
 * data URL via onSignatureChange.
 */
export default function SignaturePad({ onSignatureChange, label, clearLabel, testId = "signature-pad" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);
  const [hasSignature, setHasSignature] = useState(false);

  // Resize the canvas to match its rendered size (retina-aware).
  // Preserves the drawing: on mobile the URL bar collapse fires resize events
  // that would otherwise silently wipe the signature.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let lastWidth = 0;
    const paintBase = (ctx, rect) => {
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0f0f0f";
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, rect.width, rect.height);
    };
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      if (Math.abs(rect.width - lastWidth) < 2) return; // height-only changes (mobile URL bar) — keep drawing
      const prev = lastWidth > 0 ? canvas.toDataURL("image/png") : null;
      lastWidth = rect.width;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      paintBase(ctx, rect);
      if (prev) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
        img.src = prev;
      }
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* older browsers */ }
    drawing.current = true;
    lastPoint.current = pos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPoint.current = p;
    if (!hasSignature) setHasSignature(true);
  };

  const end = (e) => {
    if (!drawing.current) return;
    drawing.current = false;
    lastPoint.current = null;
    const canvas = canvasRef.current;
    if (e && e.pointerId != null) {
      try { canvas.releasePointerCapture(e.pointerId); } catch (err) { /* noop */ }
    }
    if (onSignatureChange) onSignatureChange(canvas.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasSignature(false);
    if (onSignatureChange) onSignatureChange("");
  };

  return (
    <div data-testid={testId}>
      {label && (
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow">{label}</span>
          {hasSignature && (
            <button
              type="button"
              onClick={clear}
              className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-400 hover:text-white flex items-center gap-1"
              data-testid={`${testId}-clear`}
            >
              <RotateCcw className="w-3 h-3" /> {clearLabel || "Clear"}
            </button>
          )}
        </div>
      )}
      <div className="border-2 border-scooter-yellow/50 bg-white touch-none" style={{ touchAction: "none" }}>
        <canvas
          ref={canvasRef}
          className="w-full h-40 cursor-crosshair touch-none select-none"
          style={{ touchAction: "none" }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          data-testid={`${testId}-canvas`}
        />
      </div>
    </div>
  );
}
